/**
 * Site-wide click tracking for GA4 (sent through window.gtag → the Google tag in GTM).
 *
 * One delegated listener classifies every link by its target, so new buttons are tracked
 * automatically and nothing is double-counted:
 *
 *   contact_click  contact_method = phone | whatsapp | email | directions | maps
 *   quote_click    quote / "Get a quote" links to the contact form (item_name for product cards)
 *
 * Every event carries link_location — the nearest ancestor's data-track-location
 * (header, tabbar, menu, contact_card, cta_panel, footer, product_card, …) — plus link_text
 * and page_path. Form submissions are tracked separately as generate_lead (Contact.js).
 *
 * Register contact_method, link_location and item_name as event-scoped custom dimensions in
 * GA4 (Admin → Custom definitions) to report on them.
 */

// GA4 is loaded by the Google tag inside GTM, which only forwards gtag() events that name it
// with send_to — without it the events stay in the dataLayer and never reach GA4.
export const GA4_MEASUREMENT_ID = 'G-BE5HY57J11';

export function trackEvent(eventName, params = {}) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', eventName, {
    ...params,
    page_path: window.location.pathname,
    send_to: GA4_MEASUREMENT_ID,
  });
}

const send = trackEvent;

function classify(anchor) {
  const href = anchor.getAttribute('href') || '';
  if (href.startsWith('tel:')) return { event: 'contact_click', method: 'phone' };
  if (href.startsWith('mailto:')) return { event: 'contact_click', method: 'email' };
  if (href.includes('wa.me/')) return { event: 'contact_click', method: 'whatsapp' };
  if (href.includes('google.com/maps/dir')) return { event: 'contact_click', method: 'directions' };
  if (href.includes('maps.app.goo.gl') || href.includes('google.com/maps')) {
    return { event: 'contact_click', method: 'maps' };
  }

  // Internal quote links: /contact#contact-form or /contact?product=…&item=…
  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch (e) {
    return null;
  }
  if (url.origin === window.location.origin && url.pathname === '/contact' && (url.hash === '#contact-form' || url.searchParams.has('item'))) {
    return { event: 'quote_click', item: url.searchParams.get('item') };
  }
  return null;
}

// Visible label of a link, with spaces between nested elements ("Business hours Mon–Thu …")
function linkText(anchor) {
  const parts = [];
  const walker = document.createTreeWalker(anchor, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) parts.push(walker.currentNode.nodeValue);
  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  return (text || anchor.getAttribute('aria-label') || '').slice(0, 80);
}

export function initClickTracking() {
  const onClick = (e) => {
    const anchor = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!anchor) return;
    const kind = classify(anchor);
    if (!kind) return;

    const location = anchor.closest('[data-track-location]')?.getAttribute('data-track-location') || 'page';
    const params = {
      link_location: location,
      link_text: linkText(anchor),
    };

    if (kind.event === 'contact_click') {
      send('contact_click', { ...params, contact_method: kind.method, link_url: anchor.href });
    } else {
      send('quote_click', { ...params, ...(kind.item ? { item_name: kind.item } : {}) });
    }
  };

  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
